import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Footer from './components/Footer';
import ApiStatusBanner from './components/ApiStatusBanner';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import CaseStudies from './pages/CaseStudies';
import Insights from './pages/Insights';
import InsightDetail from './pages/InsightDetail';
import Contact from './pages/Contact';
// 法律文书全文（28 份，中英各 14）压缩后约 60 kB。按需加载，
// 否则每一个只看首页的访客都要先下载全套条款才能看到轮播图。
const LegalIndex = React.lazy(() => import('./pages/Legal').then((m) => ({ default: m.LegalIndex })));
const LegalDoc = React.lazy(() => import('./pages/Legal').then((m) => ({ default: m.LegalDoc })));
import Careers from './pages/Careers';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/admin/PrivateRoute';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminServices from './pages/admin/AdminServices';
import AdminCaseStudies from './pages/admin/AdminCaseStudies';
import AdminNews from './pages/admin/AdminNews';
import AdminTeam from './pages/admin/AdminTeam';
import AdminCareers from './pages/admin/AdminCareers';
import AdminPressReferences from './pages/admin/AdminPressReferences';
import AdminContacts from './pages/admin/AdminContacts';

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  if (isAdmin) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
          <Route path="/admin/services" element={<PrivateRoute><AdminServices /></PrivateRoute>} />
          <Route path="/admin/case-studies" element={<PrivateRoute><AdminCaseStudies /></PrivateRoute>} />
          <Route path="/admin/news" element={<PrivateRoute><AdminNews /></PrivateRoute>} />
          <Route path="/admin/team" element={<PrivateRoute><AdminTeam /></PrivateRoute>} />
          <Route path="/admin/careers" element={<PrivateRoute><AdminCareers /></PrivateRoute>} />
          <Route path="/admin/press-references" element={<PrivateRoute><AdminPressReferences /></PrivateRoute>} />
          <Route path="/admin/contacts" element={<PrivateRoute><AdminContacts /></PrivateRoute>} />
        </Routes>
      </>
    );
  }

  if (isLoading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a192f'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Header />
      {/* 内容加载失败时的全站提示 —— 否则 API 挂掉时页面只是空着，看不出坏了 */}
      <ApiStatusBanner />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          {/* 旧的 i18n 四语条款页已下线：它们与 POLICIES/ 的新文书内容冲突，
              而一个站上同时挂两份互相矛盾的服务条款，比只有一份旧的更糟 ——
              真出纠纷时对方会挑对他有利的那份。
              nginx.conf 里有对应的 301，这里的 Navigate 只兜站内 <Link> 跳转。 */}
          <Route path="/privacy" element={<Navigate replace to="/legal/02-privacy-policy" />} />
          <Route path="/terms" element={<Navigate replace to="/legal/01-website-terms-of-use" />} />
          {/* 集团法律文书全套（POLICIES/ → src/generated/legal.js）。 */}
          <Route path="/legal" element={<Suspense fallback={<div className="spinner" />}><LegalIndex /></Suspense>} />
          <Route path="/legal/:slug" element={<Suspense fallback={<div className="spinner" />}><LegalDoc /></Suspense>} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
